// pages/ChaptersPage.tsx
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCourseStore } from '../store/courseStore';
import BackButton from '../components/ui/BackButton';

export default function ChaptersPage() {
  const { topicId, subjectId } = useParams();
  const courses = useCourseStore((s) => s.courses);
  const fetchCourses = useCourseStore((s) => s.fetchCourses);
  const isLoading = useCourseStore((s) => s.isLoading);

  useEffect(() => {
    if (courses.length === 0) fetchCourses();
  }, [courses.length, fetchCourses]);

  // Find the subject
  let subject: import('../types/course').Subject | undefined;
  for (const course of courses) {
    for (const topic of course.topics || []) {
      if (topic.id === topicId) {
        subject = topic.subjects?.find(s => s.id === subjectId);
        if (subject) break;
      }
    }
    if (subject) break;
  }

  const chapters = subject?.chapters || [];

  return (
    <div className="min-h-screen bg-surface-50 safe-top safe-bottom page-enter">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-surface-50/80 backdrop-blur-lg px-4 pt-3 pb-2">
        <BackButton label="Subjects" to="/course" />
      </div>

      <div className="px-5 pb-8 max-w-lg mx-auto">
        <h1 className="text-2xl font-extrabold text-surface-900 mb-1 animate-fade-up">
          {subject?.name || 'Chapters'}
        </h1>
        {subject?.description && (
          <p className="text-sm text-surface-300 mb-5 animate-fade-up">
            {subject.description}
          </p>
        )}

        {isLoading && chapters.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-mint-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chapters.length === 0 ? (
          <div className="text-center py-20 text-surface-300">
            <span className="text-4xl mb-3 block">📖</span>
            <p className="font-medium">No chapters yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {chapters.map((chapter, i) => {
              const videoCount = chapter.videos?.length || 0;
              const delay = Math.min(i * 60, 300);

              return (
                <Link
                  key={chapter.id}
                  to={`/course/${topicId}/${subjectId}/${chapter.id}`}
                  className="touch-card block bg-white rounded-2xl p-4 shadow-soft hover:shadow-card"
                  style={{ animation: `fadeUp 0.4s ease-out ${delay}ms both` }}
                >
                  <div className="flex items-center gap-4">
                    {/* Order Number */}
                    <div className="w-10 h-10 rounded-xl bg-lavender-50 text-lavender-600 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {chapter.order}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-surface-900 truncate">
                        {chapter.name}
                      </h3>
                      {chapter.description && (
                        <p className="text-xs text-surface-300 mt-0.5 truncate">
                          {chapter.description}
                        </p>
                      )}
                    </div>

                    {/* Count + Arrow */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-surface-300">{videoCount} lessons</span>
                      <svg className="w-4 h-4 text-surface-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
